import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Alert,
} from "@mui/material";
import { CloudUpload, Delete, Download } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

const FileUpload = ({
  onFilesSelected,
  existingFiles = [],
  maxFileSize = 100000000,
  acceptedFileTypes = ".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx",
  disabled = false,
  onFileRemoved = null,
}) => {
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    let errorMsg = "";

    for (const file of files) {
      // Check file size
      if (file.size > maxFileSize) {
        errorMsg = `File "${file.name}" exceeds maximum size of 100MB`;
        continue;
      }

      // Check file type
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const validExtensions = acceptedFileTypes.split(",").map((ext) => ext.replace(".", ""));
      if (!validExtensions.includes(fileExtension)) {
        errorMsg = `File type ".${fileExtension}" is not allowed`;
        continue;
      }

      validFiles.push(file);
    }

    if (errorMsg) {
      setError(errorMsg);
      setTimeout(() => setError(""), 5000);
    }

    // Auto-add files to parent state immediately on selection
    if (validFiles.length > 0 && onFilesSelected) {
      try {
        const filesData = validFiles.map((file) => ({
          file,
          name: file.name,
          size: file.size,
        }));

        console.log("FileUpload: Files selected and auto-saving:", filesData);
        onFilesSelected(filesData);
      } catch (err) {
        console.error("Error saving files:", err);
        setError("Error adding files");
      }
    }

    // Update local selected files display
    setSelectedFiles((prev) => [...prev, ...validFiles]);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingFile = (index) => {
    if (onFileRemoved) {
      onFileRemoved(index);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError("No files selected");
      return;
    }

    setIsUploading(true);
    setError("");

    try {
      if (onFilesSelected) {
        // Call the parent callback with the files for uploading
        const filesData = selectedFiles.map((file) => ({
          file,
          name: file.name,
          size: file.size,
        }));

        console.log("FileUpload: Sending files to parent:", filesData);
        await onFilesSelected(filesData);
        console.log("FileUpload: Parent callback completed successfully");
        setSelectedFiles([]);
        setUploadProgress(0);
      }
    } catch (err) {
      setError(err.message || "Error uploading files");
      console.error("Upload error:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = (url, filename) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <MDBox>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* File Input Area */}
      <MDBox
        onClick={() => !disabled && fileInputRef.current?.click()}
        sx={{
          border: "2px dashed",
          borderColor: disabled ? "#ccc" : "primary.main",
          borderRadius: "8px",
          p: 3,
          textAlign: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          backgroundColor: disabled ? "#f5f5f5" : "#fafafa",
          transition: "all 0.3s ease",
          "&:hover": disabled
            ? {}
            : {
                borderColor: "primary.dark",
                backgroundColor: "#f0f7ff",
              },
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedFileTypes}
          onChange={handleFileChange}
          style={{ display: "none" }}
          disabled={disabled}
        />
        <CloudUpload sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
        <MDTypography variant="body2" color="text">
          Click to upload or drag and drop
        </MDTypography>
        <MDTypography variant="caption" color="textSecondary">
          Images (JPG, PNG, GIF) or Documents (PDF, DOC, DOCX) • Max 100MB each
        </MDTypography>
      </MDBox>

      {/* Upload Progress */}
      {isUploading && uploadProgress > 0 && (
        <MDBox sx={{ mt: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <MDTypography variant="caption" sx={{ mt: 1 }}>
            Uploading... {uploadProgress}%
          </MDTypography>
        </MDBox>
      )}

      {/* Selected Files to Upload */}
      {selectedFiles.length > 0 && (
        <MDBox sx={{ mt: 2 }}>
          <MDTypography variant="h6" sx={{ mb: 1 }}>
            Selected Files ({selectedFiles.length})
          </MDTypography>
          <List dense>
            {selectedFiles.map((file, index) => (
              <ListItem
                key={`${file.name}-${index}`}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleRemoveFile(index)}
                    size="small"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemText primary={`${file.name} (${(file.size / 1024).toFixed(2)} KB)`} />
              </ListItem>
            ))}
          </List>
          {/* <MDTypography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
            ℹ️ Files will be uploaded when you click "Add Log"
          </MDTypography> */}
        </MDBox>
      )}

      {/* Existing Files
      {existingFiles.length > 0 && (
        <MDBox sx={{ mt: 3 }}>
          <MDTypography variant="h6" sx={{ mb: 1 }}>
            Uploaded Files ({existingFiles.length})
          </MDTypography>
          <List dense>
            {existingFiles.map((file, index) => (
              <ListItem
                key={`${file.filename || file.name}-${index}`}
                secondaryAction={
                  <MDBox>
                    <IconButton
                      edge="end"
                      aria-label="download"
                      onClick={() => handleDownload(file.url, file.filename || file.name)}
                      size="small"
                      title="Download"
                    >
                      <Download fontSize="small" />
                    </IconButton>
                    {onFileRemoved && (
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleRemoveExistingFile(index)}
                        size="small"
                        title="Delete"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </MDBox>
                }
              >
                <ListItemText
                  primary={file.filename || file.name}
                  secondary={
                    file.uploadedAt
                      ? new Date(file.uploadedAt).toLocaleDateString()
                      : "Recently added"
                  }
                />
              </ListItem>
            ))}
          </List>
        </MDBox>
      )} */}
    </MDBox>
  );
};

export default FileUpload;
